import {
  GetOrganizationUserResponse,
  ListUserShape,
} from "services/management-backend/client";

/**
 * @typedef {Object} User
 * @property {number} id
 * @property {string} username
 * @property {string} cognitoUsername
 * @property {number} organizationId
 * @property {string} firstName
 * @property {string} lastName
 * @property {boolean} isAdmin
 * @property {Role[]} roles
 */
type User = {
  id: number;
  username: string;
  cognitoUsername: string;
  organizationId: number;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
  isAdhoc: boolean;
  roles: Role[];
  organizationName?: string;
  isSingleAdHocAccount: boolean; // will update this once we can refactor the user to represent the domain object better
};

/**
 * @typedef {Object} Role
 * @property {number} accountId
 * @property {string} accountName
 * @property {string} role
 * @property {string} accountNumber
 * @property {boolean} isAdhoc
 */
export type Role = {
  accountId: number;
  accountName: string;
  role: string;
  accountNumber: string;
  isAdhoc: boolean;
};

/**
 * Converts a ListUserShape DTO to a User
 *
 * @param {ListUserShape} dto
 * @returns {User}
 */
export function dtoToUser(dto: ListUserShape): User {
  const isAdhoc = dto.accounts.some(({ is_adhoc }) => is_adhoc);

  // this is used in multiple places to determine if we should show certain UI elements
  // or make certain API calls
  // ideally this should be determined by the backend and sent as a property of the user
  // but for now, we are doing it here
  const isSingleAdHocAccount = dto.roles.length === 1 && isAdhoc;

  const roles: Role[] = dto.roles.map((role) => {
    const accountInfo = dto.accounts.find(
      (acc) => acc.number === role.account_number,
    );

    if (!accountInfo) {
      console.warn(`Missing account info for account number`);
      throw new Error(`Missing account info for account number`);
    }
    return {
      accountId: accountInfo.id,
      accountName: accountInfo.display_name,
      role: role.role,
      accountNumber: role.account_number,
      isAdhoc: accountInfo.is_adhoc,
    } satisfies Role;
  });

  return {
    id: dto.user.id,
    username: dto.user.username,
    cognitoUsername: dto.user.cognito_username,
    organizationId: dto.user.organization_id,
    isAdmin: dto.user.is_organization_admin,
    isAdhoc: isAdhoc,
    firstName: dto.user.first_name,
    lastName: dto.user.last_name,
    roles,
    isSingleAdHocAccount,
  } satisfies User;
}

/**
 * converts GetOrganizationUserResponse to a User
 *
 * @returns {User}
 */
export function getOrganizationUserResponseToUser(
  dto: GetOrganizationUserResponse,
): User {
  const incompleteUser = dtoToUser(dto);
  return {
    ...incompleteUser,
    organizationName: dto.organization.display_name,
  } satisfies User;
}

/**
 * Maps edit-form Role values to the account assignment shape expected by
 * IUpdateUserRequestModel (account number + role, not account id).
 *
 * @param {Role[]} roles
 * @returns {Array<{ number: string; role: string }>}
 */
export function rolesToUpdateAccountAssignments(
  roles: Role[],
): Array<{ number: string; role: string }> {
  return roles.map(({ accountNumber, role }) => ({
    number: accountNumber,
    role,
  }));
}

export default User;
